import {
  CanvasData,
  FlowEdge,
  FlowNode,
  PatientTreatmentPlanStageLog,
  detectBackEdges,
} from '../../../../../../types/healthcare/TreatmentPlanTypes';

/**
 * Frontend-only branch collapsing for decision points (practitioner_option stages).
 *
 * A decision point forks the plan: each option leads to a different branch via an `option_<key>`
 * connection. Until the practitioner has chosen, every branch is shown (a greyed roadmap); once an
 * option is recorded on the stage log (`outcome = 'option_<key>'`), the un-chosen branches are
 * hidden so the middle column shows only the path actually being taken.
 *
 * This mirrors the backend edge selection (TreatmentPlanEngine::advanceToNextStage) which follows
 * connections whose type is `default` or matches the chosen outcome — but it makes no backend or
 * data-model change. It returns the set of stage keys reachable from the plan's entry point(s)
 * given the decisions made so far.
 */
export function computeReachableStageKeys(
  canvas: CanvasData | null | undefined,
  stageLogs: PatientTreatmentPlanStageLog[],
): Set<string> {
  const reachable = new Set<string>();
  const nodes = canvas?.nodes ?? [];
  const edges = canvas?.edges ?? [];
  if (nodes.length === 0) return reachable;

  // The option chosen at each decision point, keyed by stage_key (= canvas node id). A decided
  // practitioner_option records `outcome = 'option_<key>'` on its stage log. A looped decision has one
  // log per attempt, so use the CURRENT (highest loop_iteration, then highest id) attempt's outcome.
  const bestLogByKey = new Map<string, PatientTreatmentPlanStageLog>();
  stageLogs.forEach((log) => {
    const sk = log.stage_snapshot?.stage_key ?? log.stage?.stage_key;
    if (!sk) return;
    const prev = bestLogByKey.get(sk);
    if (!prev) {
      bestLogByKey.set(sk, log);
      return;
    }
    const li = log.loop_iteration ?? 0;
    const lp = prev.loop_iteration ?? 0;
    if (li > lp || (li === lp && (log.id ?? 0) > (prev.id ?? 0))) bestLogByKey.set(sk, log);
  });
  const outcomeByKey = new Map<string, string | undefined>();
  bestLogByKey.forEach((log, sk) => outcomeByKey.set(sk, log.outcome));

  // Exclude back edges (loops) so a loop can never make an un-chosen branch reachable.
  const backEdgeIds = detectBackEdges(nodes, edges);
  const forwardEdges = edges.filter((e) => !backEdgeIds.has(e.id));

  const adjacency = new Map<string, FlowEdge[]>();
  forwardEdges.forEach((e) => {
    const list = adjacency.get(e.source) ?? [];
    list.push(e);
    adjacency.set(e.source, list);
  });

  const nodeById = new Map<string, FlowNode>(nodes.map((n) => [n.id, n]));

  // Entry points; fall back to in-degree-0 nodes if none are explicitly flagged.
  let roots = nodes.filter((n) => n.data?.is_entry_point).map((n) => n.id);
  if (roots.length === 0) {
    const hasIncoming = new Set(forwardEdges.map((e) => e.target));
    roots = nodes.filter((n) => !hasIncoming.has(n.id)).map((n) => n.id);
  }

  // Also seed from every node the plan has actually REACHED (has a log). The execution history is
  // ground truth: a node can be reached via a path the canvas can't replay forward — a loop-back
  // re-entry (its only forward incoming edge is a back-edge), or a decided branch whose forward edge
  // carries a stale connection_type. Without seeding from reached nodes, reachability stops there and
  // every FUTURE node beyond it is dropped from the roadmap. Seeding keeps the roadmap forward of
  // anything reached; decided-branch pruning still applies from each seed.
  const loggedKeys = [...bestLogByKey.keys()].filter((k) => nodeById.has(k));

  const queue = [...new Set([...roots, ...loggedKeys])];
  while (queue.length) {
    const id = queue.shift()!;
    if (reachable.has(id)) continue;
    reachable.add(id);

    const outgoing = adjacency.get(id) ?? [];
    const node = nodeById.get(id);
    const outcome = outcomeByKey.get(id);
    const isDecidedOption =
      node?.data?.stage_type === 'practitioner_option' &&
      !!outcome &&
      outcome.startsWith('option_');

    // At a DECIDED decision point follow only the chosen branch (plus any default edge) — the
    // un-chosen branches drop out of the reachable set. At an undecided decision point (and
    // everywhere else) follow every outgoing edge, so all sections downstream stay reachable.
    // Hiding the nodes that sit AFTER an undecided decision is done SECTION-LOCALLY in the middle
    // column (see trimGroupsAfterUndecidedDecision) so a decision never hides later section groups.
    const toFollow = isDecidedOption
      ? outgoing.filter(
          (e) => e.data?.connection_type === outcome || e.data?.connection_type === 'default',
        )
      : outgoing;

    toFollow.forEach((e) => {
      if (!reachable.has(e.target)) queue.push(e.target);
    });
  }

  return reachable;
}
